/**
 * Extrait les composants de date d'une chaîne ISO sans conversion de timezone
 * Ex: "2025-03-29T23:50:00+01:00" → { year: 2025, month: 3, day: 29, hour: 23, minute: 50, second: 0 }
 */
const extractDateComponents = (dateInput: Date | string): { year: number, month: number, day: number, hour: number, minute: number, second: number } | null => {
    let isoString: string;

    if (typeof dateInput === 'string') {
        isoString = dateInput;
    } else if (dateInput instanceof Date) {
        // Si c'est déjà un objet Date, on ne peut pas récupérer l'offset original
        // On utilise les méthodes locales par défaut
        return null;
    } else {
        return null;
    }

    // Regex pour parser une date ISO 8601 : 2025-03-29T23:50:00+01:00 ou 2025-03-29T22:50:00Z
    const isoRegex = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}):(\d{2}))?/;
    const match = isoString.match(isoRegex);

    if (match) {
        return {
            year: parseInt(match[1], 10),
            month: parseInt(match[2], 10),
            day: parseInt(match[3], 10),
            hour: match[4] ? parseInt(match[4], 10) : 0,
            minute: match[5] ? parseInt(match[5], 10) : 0,
            second: match[6] ? parseInt(match[6], 10) : 0
        };
    }

    return null;
};

/**
 * Retourne la date sous format JJ/MM/AAAA ou AA
 * Accepte une Date ou une chaîne ISO
*/
export const getDateFrenchFormat = (date: Date | string, isFullYear: boolean = true): string => {
    // Essayer d'extraire les composants de la chaîne ISO pour éviter la conversion timezone
    const components = extractDateComponents(date);

    if (components) {
        const day = components.day.toString().padStart(2, '0');
        const month = components.month.toString().padStart(2, '0');
        const year = isFullYear ? components.year : String(components.year).slice(-2);
        return `${day}/${month}/${year}`;
    }

    // Fallback: utiliser l'objet Date (comportement original)
    let contextDate = new Date(date)

    let day = contextDate.getDate().toString().length > 1 ? contextDate.getDate().toString() : `0${contextDate.getDate()}`
    let month = contextDate.getMonth().toString().length > 1 ? (contextDate.getMonth() + 1).toString() : (contextDate.getMonth() + 1).toString().padStart(2,'0');
    let year = (isFullYear ? contextDate.getFullYear() : String(contextDate.getFullYear()).slice(-2));

    return `${day}/${month}/${year}`;
}

/**
 * Retourne la date en format numerique YYYYMMDD
 * Accepte une Date ou une chaîne ISO
*/
export const getDateNumericFormat = (date: Date | string): number => {
    const components = extractDateComponents(date);

    if (components) {
        const day = components.day.toString().padStart(2, '0');
        const month = components.month.toString().padStart(2, '0');
        return Number(`${components.year}${month}${day}`);
    }

    let contextDate = new Date(date)

    let day = contextDate.getDate().toString().length > 1 ? contextDate.getDate().toString() : `0${contextDate.getDate()}`
    let month = contextDate.getMonth().toString().length > 1 ? (contextDate.getMonth() + 1).toString() : (contextDate.getMonth() + 1).toString().padStart(2,'0');
    let year = contextDate.getFullYear();

    return Number(`${year}${month}${day}`);
}

/**
 * Retourne l'heure au format numerique HHmmss
 * Accepte une Date ou une chaîne ISO
 */
export const getHourNumericFormat = (date: Date | string): number => {
    const components = extractDateComponents(date);

    if (components) {
        const hour = components.hour.toString().padStart(2, '0');
        const minute = components.minute.toString().padStart(2, '0');
        const second = components.second.toString().padStart(2, '0');
        return Number(`${hour}${minute}${second}`);
    }

    let contextDate = new Date(date)

    let hour = contextDate.getHours().toString().padStart(2,'0');
    let minute = contextDate.getMinutes().toString().padStart(2,'0');
    let second = contextDate.getSeconds().toString().padStart(2,'0')

    return Number(`${hour}${minute}${second}`);
}

/**
 * Retourne l'heure au format HH:mm:ss
 * Accepte une Date ou une chaîne ISO
 */
export const getHourFrenchFormat = (date: Date | string): string => {
    const components = extractDateComponents(date);

    if (components) {
        const hour = components.hour.toString().padStart(2, '0');
        const minute = components.minute.toString().padStart(2, '0');
        const second = components.second.toString().padStart(2, '0');
        return `${hour}:${minute}:${second}`;
    }

    let contextDate = new Date(date)

    let hour = contextDate.getHours().toString().padStart(2,'0');
    let minute = contextDate.getMinutes().toString().padStart(2,'0');
    let second = contextDate.getSeconds().toString().padStart(2,'0')

    return `${hour}:${minute}:${second}`;
}

/**
 * Retourne la date sous format AAAA/MM/JJ
 * Accepte une Date ou une chaîne ISO
*/
export const getDateFrenchFormatReversed = (date: Date | string): string => {
    const components = extractDateComponents(date);

    if (components) {
        const day = components.day.toString().padStart(2, '0');
        const month = components.month.toString().padStart(2, '0');
        return `${components.year}/${month}/${day}`;
    }

    let contextDate = new Date(date)

    let day = contextDate.getDate().toString().length > 1 ? contextDate.getDate().toString() : `0${contextDate.getDate()}`
    let month = contextDate.getMonth().toString().length > 1 ? (contextDate.getMonth() + 1).toString() : (contextDate.getMonth() + 1).toString().padStart(2,'0');
    let year = contextDate.getFullYear();

    return `${year}/${month}/${day}`;
}

/**
 * Retourne la date sous format AAAAMMJJ
 * Accepte une Date ou une chaîne ISO
*/
export const getDateFrenchFormatReversedWithoutSlash = (date: Date | string): string => {
    const components = extractDateComponents(date);

    if (components) {
        const day = components.day.toString().padStart(2, '0');
        const month = components.month.toString().padStart(2, '0');
        return `${components.year}${month}${day}`;
    }

    let contextDate = new Date(date)

    let day = contextDate.getDate().toString().length > 1 ? contextDate.getDate().toString() : `0${contextDate.getDate()}`
    let month = contextDate.getMonth().toString().length > 1 ? (contextDate.getMonth() + 1).toString() : (contextDate.getMonth() + 1).toString().padStart(2,'0');
    let year = contextDate.getFullYear();

    return `${year}${month}${day}`;
}

interface dateDiff {
    sec: number;
    min: number;
    hour: number;
    day: number;
}

/**
 * Retourne la différence entre deux dates
 */
export const getDateDiff = (date1 : Date, date2 : Date) => {

	var diff : dateDiff = {sec:0,min:0,hour:0,day:0}				// Initialisation du retour
	var tmp = date2.getTime() - date1.getTime();	// Soustraction des 2 dates

	tmp = Math.floor(tmp/1000);             // Nombre de secondes entre les 2 dates
	diff.sec = tmp % 60;					// Extraction du nombre de secondes

	tmp = Math.floor((tmp-diff.sec)/60);	// Nombre de minutes (partie entière)
	diff.min = tmp % 60;					// Extraction du nombre de minutes

	tmp = Math.floor((tmp-diff.min)/60);	// Nombre d'heures (entières)
	diff.hour = tmp % 24;					// Extraction du nombre d'heures
	
	tmp = Math.floor((tmp-diff.hour)/24);	// Nombre de jours restants
	diff.day = tmp;
	
	return diff;
}

export const normalizeDate = (date: string | Date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0); // Régle l'heure à minuit
    return normalized;
};

export const numberJourBetween = (dateStart: string | number | Date, dateEnd: string | number | Date) => {

    const timeDate1 = normalizeDate(new Date(dateEnd)).getTime();
    const timeDate2 = normalizeDate(new Date(dateStart)).getTime();

    const diff = Math.abs(timeDate1 - timeDate2);

    return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export type { dateDiff }